import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { PixelHeart } from "./PixelHeart";
import { tx } from "./texts";
import { findProducts } from "../data/products";
import { findTest } from "../data/data";
import { Card as TestCard } from "../router/main/component/Card";

/**
 * 검사가 아니라 '읽기 지도' (resultMode: "reading").
 *   1단계 (주소에 r 없음): 이야기를 조각별로 요약해 보여주고, 공감되는 조각에 하트
 *   2단계 (?r=101001)    : 공감 지도 + 공감한 조각마다 블로그 글을 한 번에
 *
 * 데이터 (story*.content.json)
 *   readingIntro: 1단계 안내 문장
 *   parts: [{ key, name, title, summary, fixed?, posts: [{ title, url, date, note }] }]
 *     fixed: true → 하트 없이 결과에 항상 보이는 조각
 */
const SHOW = 5; // 공감한 조각마다 보여줄 글 수 (나머지는 '모두 보기')
const SHOW_FIXED = 3;

const T = {
  ink: "#1b1b1b",
  sub: "#5f5f5f",
  mute: "#9a9a9a",
  line: "#ececec",
  paper: "#faf8f5",
  accent: "#e8861a",
  accentSoft: "#fff6ea",
};

export const decodeHearts = (value, n) =>
  Array.from({ length: n }, (_, i) => (value || "")[i] === "1");

/** 페이지가 안쪽 상자에서 스크롤되므로, 가장 가까운 스크롤 상자를 맨 위로 */
const scrollTop = (el) => {
  let node = el?.parentElement;
  while (node) {
    const st = getComputedStyle(node);
    if (/(auto|scroll)/.test(st.overflowY) && node.scrollHeight > node.clientHeight) {
      node.scrollTo({ top: 0 });
    }
    node = node.parentElement;
  }
  window.scrollTo(0, 0);
};

/* ---------- 공감 지도 값: 공감 여부 + 이웃 조각의 영향 + 링크마다 고정된 랜덤 ---------- */
const seeded = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
};

export const mapValues = (hearts, seedKey) => {
  const n = hearts.length;
  const rand = seeded(`${seedKey}:${hearts.map(Number).join("")}`);
  const count = hearts.filter(Boolean).length;
  return hearts.map((on, i) => {
    const near = (hearts[(i + 1) % n] ? 1 : 0) + (hearts[(i - 1 + n) % n] ? 1 : 0);
    const far = hearts[(i + Math.floor(n / 2)) % n] ? 1 : 0;
    let v = on ? 0.66 + rand() * 0.28 : 0.14 + rand() * 0.2;
    v += near * 0.11 + far * 0.05;
    if (on && count >= 5) v -= rand() * 0.12; // 다 공감하면 결이 납작해지지 않게
    if (!count) v = 0.22 + rand() * 0.18;
    return Math.max(0.12, Math.min(0.98, v));
  });
};

/* 클립보드: 실패해도 브라우저 대화상자를 띄우지 않음 */
const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (e2) {
      return false;
    }
  }
};

export const ReadingMap = ({ test, recommend }) => {
  const all = test.parts || [];
  const parts = all.filter((p) => !p.fixed);
  const fixed = all.filter((p) => p.fixed);
  const [params, setParams] = useSearchParams();
  const rValue = params.get("r");
  const done = rValue != null && rValue.length === parts.length;
  const ref = useRef(null);

  useEffect(() => {
    scrollTop(ref.current);
  }, [done, rValue]);

  const go = (r) => {
    const p = new URLSearchParams(params);
    if (r == null) p.delete("r");
    else p.set("r", r);
    setParams(p);
  };

  return (
    <Root ref={ref}>
      {done ? (
        <Picks test={test} parts={parts} fixed={fixed} hearts={decodeHearts(rValue, parts.length)}
          recommend={recommend} onRedo={() => go(null)} />
      ) : (
        <Check test={test} parts={parts} onDone={(h) => go(h.map((x) => (x ? 1 : 0)).join(""))} />
      )}
    </Root>
  );
};

/* ---------- 1단계: 조각 요약 + 공감 ---------- */
const Check = ({ test, parts, onDone }) => {
  const [hearts, setHearts] = useState(() => parts.map(() => false));
  const [pop, setPop] = useState({});
  const count = hearts.filter(Boolean).length;
  const toggle = (i) => {
    setHearts((h) => h.map((v, j) => (j === i ? !v : v)));
    setPop((o) => ({ ...o, [i]: (o[i] || 0) + 1 }));
  };

  return (
    <>
      <Lead>{test.readingIntro || "어느 조각에 공감했나요?"}</Lead>
      <Cards>
        {parts.map((p, i) => (
          <Piece key={p.key} $on={hearts[i]} style={{ animationDelay: `${i * 0.07}s` }}
            onClick={() => toggle(i)} role="button" tabIndex={0} aria-pressed={hearts[i]}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle(i))}>
            <PieceNo>{String(i + 1).padStart(2, "0")}</PieceNo>
            <PieceText>
              <strong>{p.title}</strong>
              <p>{p.summary}</p>
            </PieceText>
            <PieceHeart aria-hidden="true">
              <PixelHeart on={hearts[i]} pop={pop[i] || 0} size={22} line="#cfcfcf" color={T.accent} />
            </PieceHeart>
          </Piece>
        ))}
      </Cards>
      <Primary type="button" disabled={!count} onClick={() => count && onDone(hearts)}>
        {tx(test, "checkButton", { n: count })}
      </Primary>
      <Hint>{tx(test, "checkHint")}</Hint>
    </>
  );
};

/* ---------- 2단계: 지도 + 추천 글 ---------- */
const Picks = ({ test, parts, fixed, hearts, recommend, onRedo }) => {
  const [openAll, setOpenAll] = useState({});
  const values = useMemo(() => mapValues(hearts, test.id), [hearts, test.id]);
  const picked = parts.filter((_, i) => hearts[i]);
  const strongest = parts[values.indexOf(Math.max(...values))];
  const limit = (part, n) => (openAll[part.key] ? part.posts : part.posts.slice(0, n));
  const groups = [
    ...parts.map((part, i) => ({ part, i })).filter((x) => hearts[x.i]).map((x) => ({ ...x, posts: limit(x.part, SHOW), cap: SHOW })),
    ...fixed.map((part) => ({ part, i: -1, posts: limit(part, SHOW_FIXED), cap: SHOW_FIXED })),
  ];
  const products = findProducts(recommend?.products || []);
  const tests = (recommend?.tests || []).map(findTest).filter(Boolean);

  return (
    <>
      <Title>
        {picked.length
          ? tx(test, "mapTitle", { name: "\u0000" }).split("\u0000").map((chunk, i) =>
              i ? <React.Fragment key={i}><em>{strongest.name}</em>{chunk}</React.Fragment> : chunk)
          : tx(test, "mapTitleNone")}
      </Title>
      <Radar parts={parts} values={values} hearts={hearts} />
      <MapFoot>
        <span>{tx(test, "mapCount", { n: picked.length, total: parts.length })}</span>
        <TextBtn type="button" onClick={onRedo}>{tx(test, "redo")}</TextBtn>
      </MapFoot>

      {groups.map((g) => (
        <Section key={g.part.key}>
          <SectionHead>
            {g.i < 0 && <Chip $soft>{tx(test, "chipAll")}</Chip>}
            <h3>{g.part.title}</h3>
            {g.i >= 0 && <p>{g.part.summary}</p>}
          </SectionHead>
          <LinkList>
            {g.posts.map((post) => <LinkCard key={post.url} post={post} label={tx(test, "thumbLabel")} />)}
          </LinkList>
          {(g.part.posts.length > g.cap) && (
            <TextBtn type="button" onClick={() => setOpenAll((o) => ({ ...o, [g.part.key]: !o[g.part.key] }))}>
              {openAll[g.part.key] ? tx(test, "less") : tx(test, "more", { n: g.part.posts.length - g.cap })}
            </TextBtn>
          )}
        </Section>
      ))}

      {products.length > 0 && (
        <Section>
          <SectionHead>
            <Chip $soft>{tx(test, "productsChip")}</Chip>
            <h3>{tx(test, "productsTitle")}</h3>
          </SectionHead>
          <Products>
            {products.map((p) => (
              <Product key={p.id} href={p.url} target="_blank" rel="noopener noreferrer">
                <small>{p.label}</small>
                <strong>{p.title}</strong>
                <span>{p.desc}</span>
              </Product>
            ))}
          </Products>
          {products.some((p) => p.coupang) && (
            <Fine>{tx(test, "coupangNote")}</Fine>
          )}
        </Section>
      )}

      {tests.length > 0 && (
        <Section>
          <LinkList>
            {tests.map((t) => <TestCard key={t.id} cardItem={t} />)}
          </LinkList>
        </Section>
      )}
    </>
  );
};

/** 네이버 블로그 본문 속 링크 미리보기처럼 */
const LinkCard = ({ post, label }) => {
  const year = (post.date || "").slice(0, 4);
  return (
    <Card href={post.url} target="_blank" rel="noopener noreferrer">
      <CardText>
        <strong>{post.title}</strong>
        {post.note && <p>{post.note}</p>}
        <span>
          <i aria-hidden="true">N</i>blog.naver.com/moistmarketer{post.date ? ` · ${post.date}` : ""}
        </span>
      </CardText>
      <Thumb aria-hidden="true">
        <b>{year || "MOIST"}</b>
        <small>{label}</small>
      </Thumb>
    </Card>
  );
};

/** 공감 지도 (조각 수만큼 꼭짓점) */
const Radar = ({ parts, values, hearts }) => {
  const n = parts.length;
  const S = 320, C = 160, R = 104;
  const pt = (i, r) => {
    const a = ((-90 + (360 / n) * i) * Math.PI) / 180;
    return [C + r * Math.cos(a), C + r * Math.sin(a)];
  };
  const ring = (k) => parts.map((_, i) => pt(i, R * k).join(",")).join(" ");
  const poly = values.map((v, i) => pt(i, R * v).join(",")).join(" ");
  // 바깥 잔상: 값이 살짝 흔들린 두 번째 결
  const echo = values.map((v, i) => pt(i, R * Math.min(1, v * (0.82 + ((i * 37) % 11) / 60))).join(",")).join(" ");

  return (
    <RadarBox>
      <svg viewBox={`0 0 ${S} ${S}`} role="img" aria-label="공감 지도">
        {[0.25, 0.5, 0.75, 1].map((k) => (
          <polygon key={k} points={ring(k)} fill="none" stroke={k === 1 ? "#e4e0da" : "#efebe6"} strokeWidth="1" />
        ))}
        {parts.map((_, i) => {
          const [x, y] = pt(i, R);
          return <line key={i} x1={C} y1={C} x2={x} y2={y} stroke="#efebe6" />;
        })}
        <g className="echo" style={{ transformOrigin: `${C}px ${C}px` }}>
          <polygon points={echo} fill="rgba(232,134,26,0.07)" stroke="rgba(232,134,26,0.35)" strokeDasharray="3 4" />
        </g>
        <g className="shape" style={{ transformOrigin: `${C}px ${C}px` }}>
          <polygon points={poly} fill="rgba(232,134,26,0.16)" stroke={T.accent} strokeWidth="2" strokeLinejoin="round" />
        </g>
        {values.map((v, i) => {
          const [x, y] = pt(i, R * v);
          return <circle key={i} className="dot" style={{ animationDelay: `${0.45 + i * 0.07}s` }}
            cx={x} cy={y} r={hearts[i] ? 4.5 : 3} fill={hearts[i] ? T.accent : "#fff"} stroke={T.accent} strokeWidth="1.5" />;
        })}
        {parts.map((p, i) => {
          const [x, y] = pt(i, R + 30);
          return (
            <g key={p.key}>
              <text x={x} y={y - 2} textAnchor="middle" fontSize="13" fontWeight={hearts[i] ? 600 : 400}
                fill={hearts[i] ? T.ink : "#8d8d8d"}>{p.name}</text>
              <text x={x} y={y + 14} textAnchor="middle" fontSize="12" fill={hearts[i] ? T.accent : "#b5b5b5"}>
                {Math.round(values[i] * 100)}
              </text>
            </g>
          );
        })}
      </svg>
    </RadarBox>
  );
};

/* ---------- 페이지 맨 아래: 한 번에 저장하기 ---------- */
export const ReadingSave = ({ test, rValue }) => {
  const [msg, setMsg] = useState("");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const all = test.parts || [];
  const parts = all.filter((p) => !p.fixed);
  const hearts = decodeHearts(rValue, parts.length);
  const shown = [
    ...parts.filter((_, i) => hearts[i]).map((part) => ({ part, posts: part.posts.slice(0, SHOW) })),
    ...all.filter((p) => p.fixed).map((part) => ({ part, posts: part.posts.slice(0, SHOW_FIXED) })),
  ];
  const count = shown.reduce((n, x) => n + x.posts.length, 0);
  if (!count) return null;

  const flash = (t) => {
    setMsg(t);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(""), 2000);
  };
  const asPlain = () =>
    shown.map((x) => `■ ${x.part.title}\n` + x.posts.map((p) => `- ${p.title}\n  ${p.url}`).join("\n")).join("\n\n");
  const asMarkdown = () =>
    `## ${test.title} · 읽을 글\n\n` +
    shown.map((x) => `### ${x.part.title}\n` + x.posts.map((p) => `- [ ] [${p.title}](${p.url})`).join("\n")).join("\n\n");
  const saveFile = () => {
    try {
      const blob = new Blob([`${test.title} · 읽을 글\n${window.location.href}\n\n${asPlain()}\n`], {
        type: "text/plain;charset=utf-8",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${tx(test, "fileName")}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      flash(tx(test, "toastTxt"));
    } catch (e) {
      flash(tx(test, "toastFail"));
    }
  };
  const copyBtn = (label, make, ok) => (
    <SaveBtn type="button" onClick={async () => flash((await copyText(make())) ? ok : tx(test, "toastFail"))}>
      {label}
    </SaveBtn>
  );
  return (
    <SaveBox>
      <Eyebrow>{tx(test, "saveEyebrow")}</Eyebrow>
      <h3>{tx(test, "saveTitle", { n: count })}</h3>
      <div>
        {copyBtn(tx(test, "saveCopyAll"), asPlain, tx(test, "toastCopyAll"))}
        {copyBtn(tx(test, "saveNotion"), asMarkdown, tx(test, "toastNotion"))}
        <SaveBtn type="button" onClick={saveFile}>{tx(test, "saveTxt")}</SaveBtn>
        {copyBtn(tx(test, "saveLink"), () => window.location.href, tx(test, "toastLink"))}
      </div>
      {msg && <Msg role="status">{msg}</Msg>}
    </SaveBox>
  );
};

/* ---------- 스타일 ---------- */
const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: none; }
`;
const grow = keyframes`
  0%   { transform: scale(0.15) rotate(-24deg); opacity: 0; }
  60%  { transform: scale(1.04) rotate(2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
`;
const pop = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const calm = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

const Root = styled.section`
  width: 100%;
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  color: ${T.ink};
  word-break: keep-all;
`;
const Eyebrow = styled.div`
  font-size: 13px;
  letter-spacing: 0.08em;
  color: ${T.accent};
`;
const Lead = styled.p`
  margin: 10px 0 0;
  font-size: 17px;
  line-height: 1.8;
  white-space: pre-line;
`;
const Title = styled.h2`
  margin: 10px 0 0;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.45;
  em { font-style: normal; font-weight: 700; color: ${T.accent}; }
`;
const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 28px;
`;
const Piece = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 18px;
  border-radius: 16px;
  border: 1px solid ${(p) => (p.$on ? "#f0c48f" : T.line)};
  background: ${(p) => (p.$on ? T.accentSoft : "#fff")};
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.15s;
  animation: ${rise} 0.45s ease both;
  &:active { transform: scale(0.99); }
  ${calm}
`;
const PieceNo = styled.span`
  flex: none;
  padding-top: 2px;
  font-size: 13px;
  letter-spacing: 0.05em;
  color: ${T.mute};
`;
const PieceText = styled.div`
  flex: 1;
  min-width: 0;
  strong { display: block; font-size: 16.5px; font-weight: 600; line-height: 1.5; }
  p { margin: 8px 0 0; font-size: 15px; line-height: 1.75; color: ${T.sub}; white-space: pre-line; }
`;
const PieceHeart = styled.span`
  flex: none;
  padding-top: 3px;
`;
const Primary = styled.button`
  margin-top: 28px;
  padding: 17px;
  border: 0;
  border-radius: 14px;
  background: ${T.ink};
  color: #fff;
  font-size: 16px;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: opacity 0.2s;
  &:disabled { opacity: 0.28; cursor: default; }
`;
const Hint = styled.p`
  margin: 12px 0 0;
  text-align: center;
  font-size: 14px;
  color: ${T.mute};
`;
const RadarBox = styled.div`
  margin-top: 22px;
  padding: 18px 8px 6px;
  border-radius: 20px;
  background: ${T.paper};
  svg { display: block; width: 100%; max-width: 340px; margin: 0 auto; overflow: visible; }
  .shape { animation: ${grow} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
  .echo { animation: ${grow} 1.2s 0.15s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
  .dot { animation: ${pop} 0.3s ease both; }
  ${calm}
  .shape, .echo, .dot { ${calm} }
`;
const MapFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding: 0 4px;
  font-size: 14px;
  color: ${T.mute};
`;
const TextBtn = styled.button`
  align-self: flex-start;
  margin-top: 14px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 14px;
  color: ${T.sub};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  ${MapFoot} & { margin-top: 0; }
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 48px;
`;
const SectionHead = styled.div`
  margin-bottom: 16px;
  h3 { margin: 10px 0 0; font-size: 19px;
  }
  h3:first-child { margin-top: 0; }
  h3 { font-weight: 600; line-height: 1.5; }
  p { margin: 6px 0 0; font-size: 15px; line-height: 1.75; color: ${T.sub}; white-space: pre-line; }
`;
const Chip = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12.5px;
  color: ${(p) => (p.$soft ? T.sub : T.accent)};
  background: ${(p) => (p.$soft ? "#f2f0ec" : T.accentSoft)};
`;
const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Card = styled.a`
  display: flex;
  gap: 14px;
  padding: 16px;
  border: 1px solid ${T.line};
  border-radius: 14px;
  background: #fff;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover { border-color: #ddd; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05); }
`;
const CardText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  strong {
    font-size: 15.5px;
    font-weight: 600;
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  p {
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.7;
    color: ${T.sub};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  span {
    margin-top: auto;
    padding-top: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: ${T.mute};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  i {
    flex: none;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    background: #03c75a;
    color: #fff;
    font-style: normal;
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;
const Thumb = styled.div`
  flex: none;
  width: 84px;
  height: 84px;
  align-self: center;
  border-radius: 10px;
  background: linear-gradient(150deg, #fff3e2 0%, #fde3c2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  b { font-size: 19px; font-weight: 600; letter-spacing: 0.02em; color: #b8661a; }
  small { font-size: 10.5px; letter-spacing: 0.06em; color: #c99a6a; }
`;
const Products = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 360px) { grid-template-columns: 1fr; }
`;
const Product = styled.a`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border: 1px solid ${T.line};
  border-radius: 14px;
  color: inherit;
  text-decoration: none;
  small { font-size: 12.5px; color: ${T.accent}; }
  strong { font-size: 15px; font-weight: 600; line-height: 1.5; }
  span { font-size: 13.5px; line-height: 1.6; color: ${T.sub}; }
  &:hover { border-color: #ddd; }
`;
const Fine = styled.p`
  margin: 12px 2px 0;
  font-size: 12.5px;
  line-height: 1.6;
  color: ${T.mute};
`;
const SaveBox = styled.div`
  width: 100%;
  margin-top: 40px;
  padding: 24px 20px;
  border-radius: 18px;
  background: ${T.paper};
  color: ${T.ink};
  h3 { margin: 8px 0 16px; font-size: 18px; font-weight: 600; }
  > div { display: flex; flex-wrap: wrap; gap: 8px; }
`;
const SaveBtn = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e2ddd5;
  background: #fff;
  color: ${T.ink};
  font-size: 14px;
  cursor: pointer;
  &:hover { border-color: ${T.accent}; }
`;
const Msg = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: ${T.accent};
`;
